import { useTranslation } from "react-i18next"
import styled from 'styled-components'
import { Checkbox, Form, Select } from 'antd'
import { FormReservationType, GenderOptionType, ToggleFeaturesType } from '../types'
import { LabelBox } from '../components/base-ui'
import PhoneInput from 'react-phone-input-2'

interface SelectCourseProps {
  reservationFormData?: FormReservationType
  phoneCountry: string
  setPhoneCountry: (phoneCountry: string) => void
  bookingFeatures: ToggleFeaturesType
  genderOptions: GenderOptionType[]
  onClickBack: () => void
  onClickNext: (formValue: FormReservationType) => void
}

const SelectCourseReservation = ({ reservationFormData, phoneCountry, setPhoneCountry, bookingFeatures, genderOptions, onClickBack, onClickNext }: SelectCourseProps) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormReservationType>();
  const is_checked_additional_request = Form.useWatch('additional_request', form);
  const is_checked_special_occasion = Form.useWatch('special_occasion', form);
  const is_checked_dietary_restriction = Form.useWatch('dietary_restriction', form);
  // Gated by BOOKING_GENDER_FIELD ( see services/_features_gate.php ).
  const is_gender_enabled = bookingFeatures.booking_gender_field && genderOptions.length > 0;

  const onFinish = (values: FormReservationType) => {
    onClickNext(values)
  }

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo)
  }

  return (
    <Wrapper id="step-reservation-detail">
      <Form 
        form={form}
        className="yz-form" 
        autoComplete="off"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{ ...reservationFormData }}
      >
        <div className="input-group">
          <div>
            <div className="yz-text-sm yz-input-label">{t('first_name')}*</div>
            <div className="yz-input-wrapper">
              <Form.Item
                name="firstname"
                rules={[{ required: true, message: `${t('first_name_validate_empty')}!` }]}
              >
                <input type="text" placeholder={t('first_name_placeholder')} />
              </Form.Item>
            </div>
          </div>
          <div>
            <div className="yz-text-sm yz-input-label">{t('last_name')}*</div>
            <div className="yz-input-wrapper">
              <Form.Item
                name="lastname"
                rules={[{ required: true, message: `${t('last_name_validate_empty')}!` }]}
              >
                <input type="text" placeholder={t('last_name_placeholder')} />
              </Form.Item>
            </div>
          </div>
        </div>
        <div className="yz-text-sm yz-input-label">{t('email_address')}*</div>
        <div className="yz-input-wrapper">
          <Form.Item
            name="email"
            rules={[{ required: true, message: `${t('email_address_validate_empty')}!` }, { type: 'email', message: `${t('email_address_validate_invalid')}!` }]}
          >
            <input type="email" placeholder={t('email_address_placeholder')} />
          </Form.Item>
        </div>

        <div className="yz-text-sm yz-input-label">{t('phone')}*</div>
        <div className="yz-input-wrapper yuzu-input-phone">
          <Form.Item
            name="phone"
            rules={[
              { required: true, message: `${t('phone_validate_empty')}!` }, 
              { pattern: new RegExp("^[0-9]+$"), message: `${t('phone_validate_invalid')}!`}
            ]}
          >
            <PhoneInput 
              country={phoneCountry} 
              placeholder={t('phone_placeholder')}
              onChange={(value, country) => {
                const phoneData = country as any
                setPhoneCountry(phoneData.countryCode)
              }} 
              autoFormat={false}
            />
          </Form.Item>
        </div>

        {
          is_gender_enabled && (
            <>
              <div className="yz-text-sm yz-input-label">{t('gender')}*</div>
              <div className="yz-input-wrapper">
                <Form.Item
                  name="gender"
                  rules={[{ required: true, message: `${t('gender_validate_empty')}!` }]}
                >
                  <Select placeholder={t('gender_placeholder')}>
                    {genderOptions.map(genderOption => (
                      <Select.Option key={genderOption.value} value={genderOption.value}>{genderOption.label}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
            </>
          )
        }

        <div className="yz-input-wrapper -checkbox">
          <Form.Item
            name="additional_request"
            valuePropName="checked"
          >
            <Checkbox>{t('additional_request')}</Checkbox>
          </Form.Item>
        </div>
        {
          is_checked_additional_request && (
            <div className="yz-input-wrapper">
              <Form.Item
                name="additional_request_details"
                rules={[{ required: true, message: `${t('option_validate_empty')}!` }]}
              >
                <textarea rows={3} placeholder={t('option_placeholder')}></textarea>
              </Form.Item>
            </div>
          )
        }

        <div className="yz-input-wrapper -checkbox">
          <Form.Item
            name="special_occasion"
            valuePropName="checked"
          >
            <Checkbox>{t('special_occasion')}</Checkbox>
          </Form.Item>
        </div>
        {
          is_checked_special_occasion && (
            <div className="yz-input-wrapper">
              <Form.Item
                name="special_occasion_details"
                rules={[{ required: true, message: `${t('option_validate_empty')}!` }]}
              >
                <textarea rows={3} placeholder={t('option_placeholder')}></textarea>
              </Form.Item>
            </div>
          )
        }

        <div className="yz-input-wrapper -checkbox">
          <Form.Item
            name="dietary_restriction"
            valuePropName="checked"
          >
            <Checkbox>{t('dietary_restriction')}</Checkbox>
          </Form.Item>
        </div>
        {
          is_checked_dietary_restriction && (
            <div className="yz-input-wrapper">
              <Form.Item
                name="dietary_restriction_details"
                rules={[{ required: true, message: `${t('option_validate_empty')}!` }]}
              >
                <textarea rows={3} placeholder={t('option_placeholder')}></textarea>
              </Form.Item>
            </div>
          )
        }
        <LabelBoxWrapper style={{ marginBottom: '24px' }}>
          <LabelBox type="warning">
            {t('notice_kindly_tell_us_allergy')}
          </LabelBox>
        </LabelBoxWrapper>

        <Footer>
          <div className="yuzu-btn -outline -lg" onClick={onClickBack}><span>{t('back')}</span></div>
          <div className="yuzu-btn -lg" onClick={() => form.submit()}><span>{t('next')}</span></div>
        </Footer>
      </Form>
    </Wrapper>
  )
}

export default SelectCourseReservation

const Wrapper = styled.div`
  max-width: 500px;

  .yz-input-label {
    @media (max-width: 767px) {
      font-size: 12px;
    }
  }

  .yuzu-input-phone {
    .react-tel-input .flag-dropdown {
      background-color: transparent !important;
      border: 0;
    }

    .react-tel-input .selected-flag {
      width: 50px;
      padding-left: 14px;
      background-color: transparent !important;

      .flag {
        transform: scale(1.3);
      }
    }

    .react-tel-input .selected-flag:hover, .react-tel-input .selected-flag:focus {
      background-color: transparent;
    }

    .react-tel-input .form-control {
      height: 50px;
      border: 0;
      width: 100%;
      padding-left: 54px !important;
      border-radius: 8px;

      @media (max-width: 1024px) {
        height: 40px;
      }
    }
  }
`

const LabelBoxWrapper = styled.div`
  color: white;
`

const Footer = styled.div`
  margin-top: 64px;
  margin-bottom: 80px;
  display: flex;
  justify-content: space-between;
`
